if (!customElements.get('pdp-v2-colours')) {
  customElements.define(
    'pdp-v2-colours',
    class PdpV2Colours extends HTMLElement {
      cache = new Map();
      requestToken = 0;

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.sectionId = this.dataset.section;
        this.stickySectionId = 'sticky-atc-bar';

        this.addEventListener('click', this.onClick);
        this.addEventListener('mouseover', this.onPrefetch);
        this.addEventListener('focusin', this.onPrefetch);
        document.addEventListener('pdp-v2:colour-change', this.onColourChange);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('mouseover', this.onPrefetch);
        this.removeEventListener('focusin', this.onPrefetch);
        document.removeEventListener('pdp-v2:colour-change', this.onColourChange);
      }

      // Keeps every colour list on the page (main block + sticky bar) on the same active swatch
      onColourChange = (event) => {
        if (!event.detail?.url) return;
        const path = new URL(event.detail.url, window.location.origin).pathname;
        const link = [...this.querySelectorAll('.pdp-v2-colours__link')].find(
          (el) => new URL(el.href, window.location.origin).pathname === path
        );
        const item = link?.closest('.pdp-v2-colours__item');
        if (item && !item.classList.contains('is-active')) this.setActive(item, link);
      };

      onClick = (event) => {
        const link = event.target.closest('.pdp-v2-colours__link');
        if (!link || !this.contains(link) || !this.productInfo) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        const item = link.closest('.pdp-v2-colours__item');
        if (item.classList.contains('is-active')) return;

        this.selectColour(item, link);
      };

      onPrefetch = (event) => {
        const link = event.target.closest?.('.pdp-v2-colours__link');
        if (!link || !this.contains(link)) return;
        if (link.closest('.pdp-v2-colours__item')?.classList.contains('is-active')) return;
        this.fetchSections(link.href).catch(() => {});
      };

      buildRequestUrl(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        url.search = '';
        if (this.dataset.view) url.searchParams.set('view', this.dataset.view);
        url.searchParams.set('sections', `${this.sectionId},${this.stickySectionId}`);
        return url.toString();
      }

      fetchSections(productUrl) {
        const requestUrl = this.buildRequestUrl(productUrl);
        if (!this.cache.has(requestUrl)) {
          const request = fetch(requestUrl)
            .then((response) => {
              if (!response.ok) throw new Error(`Failed to load ${requestUrl}`);
              return response.json();
            })
            .catch((error) => {
              this.cache.delete(requestUrl);
              throw error;
            });
          this.cache.set(requestUrl, request);
        }
        return this.cache.get(requestUrl);
      }

      setActive(item, link) {
        this.querySelectorAll('.pdp-v2-colours__item').forEach((el) => {
          el.classList.toggle('is-active', el === item);
          el.querySelector('.pdp-v2-colours__link')?.removeAttribute('aria-current');
        });
        link.setAttribute('aria-current', 'true');

        const label = this.querySelector('.pdp-v2-colours__label');
        const labelValue = this.querySelector('.pdp-v2-colours__label-value');
        const colourName = link.dataset.colorName || '';
        if (labelValue) labelValue.textContent = colourName;
        if (label) label.hidden = colourName === '';

        const sizeLabelColour = this.productInfo?.querySelector('.pdp-v2__size-label-colour');
        if (sizeLabelColour) {
          sizeLabelColour.textContent = colourName ? ` - ${colourName}` : '';
          sizeLabelColour.hidden = colourName === '';
        }
      }

      async selectColour(item, link) {
        const token = ++this.requestToken;
        const previousItem = this.querySelector('.pdp-v2-colours__item.is-active');
        const previousLink = previousItem?.querySelector('.pdp-v2-colours__link');

        this.setActive(item, link);
        this.productInfo.classList.add('pdp-v2--swapping');

        let sections;
        try {
          sections = await this.fetchSections(link.href);
        } catch (error) {
          console.error(error);
        }

        if (token !== this.requestToken) return;

        const sectionHtml = sections?.[this.sectionId];
        if (!sectionHtml) {
          if (previousItem && previousLink) this.setActive(previousItem, previousLink);
          this.productInfo.classList.remove('pdp-v2--swapping');
          window.location.href = link.href;
          return;
        }

        const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newInfo = html.querySelector('product-info');
        if (newInfo) {
          this.productInfo.setAttribute('data-product-id', newInfo.dataset.productId);
          this.productInfo.setAttribute('data-url', newInfo.dataset.url);
          this.productInfo
            .querySelector(`#ProductInfo-${this.sectionId}`)
            ?.setAttribute('data-url', newInfo.dataset.url);
        }

        this.updateMedia(html);
        this.updateBuyButtons(html);
        this.updatePdpStickyAtc(html);
        this.updateStickyBar(sections[this.stickySectionId]);

        window?.Shopify?.PaymentButton?.init();
        this.productInfo.classList.remove('pdp-v2--swapping');

        document.dispatchEvent(
          new CustomEvent('pdp-v2:colour-change', {
            detail: { productId: newInfo?.dataset.productId, url: newInfo?.dataset.url },
          })
        );
      }

      updateMedia(html) {
        const productWrapper = this.productInfo.querySelector('.pdp-v2-shell > .product');
        const newProductWrapper = html.querySelector('.pdp-v2-shell > .product');
        if (!productWrapper || !newProductWrapper) return;

        const hasCustomRatio = productWrapper.classList.contains('product--custom-ratio');
        const forcedRatio = getComputedStyle(productWrapper).getPropertyValue('--pdp-v2-forced-ratio').trim();

        productWrapper.className = newProductWrapper.className;
        productWrapper.classList.toggle('product--custom-ratio', hasCustomRatio);

        const oldMedia = productWrapper.querySelector('.pdp-v2__media');
        const newMediaSource = newProductWrapper.querySelector('.pdp-v2__media');
        const newMedia = newMediaSource ? this.toLiveNode(newMediaSource) : null;

        if (newMedia) this.applyGalleryRatio(newMedia, hasCustomRatio ? forcedRatio : '');

        if (oldMedia && newMedia) {
          oldMedia.replaceWith(newMedia);
        } else if (newMedia) {
          productWrapper.prepend(newMedia);
        } else if (oldMedia) {
          oldMedia.remove();
        }
      }

      applyGalleryRatio(mediaNode, forcedRatio) {
        mediaNode.querySelectorAll('.product__media-container').forEach((container) => {
          if (forcedRatio) {
            container.classList.add('product__media-container--ratio-override');
            container.style.setProperty('--aspect-ratio', forcedRatio);
          } else if (container.classList.contains('product__media-container--ratio-override')) {
            container.classList.remove('product__media-container--ratio-override');
            const naturalRatio = container.closest('.product__media-item')?.dataset.aspectRatio;
            if (naturalRatio) container.style.setProperty('--aspect-ratio', naturalRatio);
          }
        });
      }

      updateBuyButtons(html) {
        ['.pdp-v2__block--price', '.pdp-v2__block--buy-buttons'].forEach((selector) => {
          const oldBlock = this.productInfo.querySelector(selector);
          const newBlock = html.querySelector(selector);
          if (oldBlock && newBlock) oldBlock.replaceWith(this.toLiveNode(newBlock));
        });
      }

      updatePdpStickyAtc(html) {
        const selector = `#PdpV2StickyAtc-${this.sectionId}`;
        const oldSticky = this.productInfo.querySelector(selector);
        const newSticky = html.querySelector(selector);
        if (!oldSticky || !newSticky) return;

        const liveSticky = this.toLiveNode(newSticky);
        ['is-visible', 'has-colours'].forEach((cls) => {
          liveSticky.classList.toggle(cls, oldSticky.classList.contains(cls));
        });
        oldSticky.replaceWith(liveSticky);
      }

      updateStickyBar(stickyHtml) {
        const oldSection = document.getElementById(`shopify-section-${this.stickySectionId}`);
        if (!oldSection || !stickyHtml) return;

        const html = new DOMParser().parseFromString(stickyHtml, 'text/html');
        const newSection = html.getElementById(`shopify-section-${this.stickySectionId}`);
        if (newSection) oldSection.replaceWith(this.toLiveNode(newSection));
      }

      // Parse into the live document so custom elements (incl. `is=` built-ins) upgrade.
      toLiveNode(element) {
        const wrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(wrapper, element.outerHTML);
        return wrapper.firstElementChild;
      }
    }
  );
}
